import { isPdf, ResponsivePdfViewer } from "@/components/ui/responsive-pdf-viewer";
import { archiveTitle, ArchiveTitleEditor } from "./ArchivePresentation";
import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import { recordLabel } from "@/lib/employeeRecordLabels";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import {
  renameEmployeeDocument, dohvatiPodatkeDokumenta, dohvatiSadrzajDokumenta,
  type DokumentDjelatnika, type EmployeeDocumentDetails, type DocumentRecordDetails,
} from "@/lib/dokumenti";
import { datum } from "@/lib/format";

const DESTINATIONS: Record<string, { tab: string; permission: string; action?: string }> = {
  dozvola: { tab: "dozvole", permission: "dozvola" },
  zdravstvena_sposobnost: { tab: "zdravlje", permission: "zdravlje" },
  osposobljavanje_znr: { tab: "znr", permission: "znr" },
  ozo_zaduzenje: { tab: "znr", permission: "znr" },
  vozacka_dozvola: { tab: "identitet", permission: "dozvola" },
  identifikacijski_dokument: { tab: "identitet", permission: "dozvola" },
  kvalifikacija: { tab: "kvalifikacije", permission: "radnik" },
  employee_employment_terms: { tab: "payroll", permission: "payroll_profile", action: "write" },
};

/** Values are the server's permission-filtered business projection, never a payroll profile. */
export function DocumentDataPanel({ radnikId, documentId, document: initial, showPreview = false, onEdit, refreshKey = 0, showEdit = true, expectedRecordId, expectedRecordKind, onDocumentUpdated }: {
  radnikId: string;
  documentId: string;
  document?: DokumentDjelatnika;
  showPreview?: boolean;
  refreshKey?: number;
  showEdit?: boolean;
  expectedRecordId?: string;
  expectedRecordKind?: string;
  onDocumentUpdated?: (document: DokumentDjelatnika) => void;
  onEdit?: (record: DocumentRecordDetails) => void;
}) {
  const { can } = useAuth();
  const [response, setResponse] = useState<EmployeeDocumentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    dohvatiPodatkeDokumenta(radnikId, documentId, controller.signal).then((details) => {
      if (!controller.signal.aborted) { setResponse(details); setError(null); }
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted) setError(errorMessage(failure, "Podaci dokumenta nisu dostupni."));
    });
    return () => controller.abort();
  }, [radnikId, documentId, initial?.verificirano_at, refreshKey]);
  const details = response?.document.id === documentId && response.document.radnik_id === radnikId ? response : null;
  const document = details?.document ?? initial;
  const rawRecord = details?.structured_record_restricted ? null : details?.structured_record;
  const destination = DESTINATIONS[rawRecord?.kind ?? ""];
  const recordMatches = (!expectedRecordId || rawRecord?.id === expectedRecordId) && (!expectedRecordKind || rawRecord?.kind === expectedRecordKind);
  const record = recordMatches && rawRecord && destination && can(destination.permission, "read") ? rawRecord : null;
  const canEdit = showEdit && record && record.can_edit && can(destination!.permission, destination!.action ?? "update");
  const evidenceQuery = record?.kind === "employee_employment_terms" ? `&document=${encodeURIComponent(documentId)}` : "";
  const values = document ? [
    ["Naslov", archiveTitle(document)],
    ["Naziv datoteke", document.original_filename],
    ["Tip", document.document_type_name || document.document_type_code],
    ["Izdano", document.issued_on ? datum(document.issued_on) : null],
    ["Istek", document.trajno ? "Trajno" : document.expires_on ? datum(document.expires_on) : null],
    ["Provjera", document.confirmation_label ?? (document.verificiran ? "Provjeren" : "Nije provjeren")],
    ["Provjerio", details?.verificirao_name || document.verificirao_id],
    ["Provjereno", document.verificirano_at ? new Date(document.verificirano_at).toLocaleString("hr-HR") : null],
    ["Opis", document.opis], ["Napomena arhive", document.napomena],
  ] : [];
  return <>
    <section aria-label="Podaci dokumenta" className="avm-surface min-w-0 space-y-2 rounded border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">Podaci dokumenta</h2>
        {canEdit && (onEdit ? <Button variant="outline" onClick={() => onEdit(record!)}>Uredi</Button> :
          <Link className={buttonVariants({ variant: "secondary", size: "sm" })} to={`/zaposlenici/${radnikId}?tab=${destination!.tab}${evidenceQuery}#record-${record!.id}`}>Uredi</Link>)}
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {!document && !error && <p role="status">Učitavanje podataka…</p>}
      {document && showEdit && document.status !== "deleted" && can("dokument", "update") &&
        (!["ra2_medical_fitness", "pur_medical_certificate"].includes(document.document_type_code || "") || can("zdravlje", "update")) &&
        <ArchiveTitleEditor title={archiveTitle(document)} save={async value => {
          const updated = await renameEmployeeDocument(radnikId, documentId, value);
          setResponse(current => current ? { ...current, document: updated } : null);
          onDocumentUpdated?.(updated);
        }} />}
      <PropertyList>
        {values.map(([label, value]) => <PropertyItem key={label} label={label}>{value || "—"}</PropertyItem>)}
      </PropertyList>
      {details && !details.structured_record_restricted && !recordMatches && <p role="alert">Traženi povezani zapis nije dostupan uz ovaj dokument.</p>}
      {record && <div className="border-t pt-2"><h3 className="mb-1 text-sm font-semibold">Povezani zapis</h3><PropertyList>
        {record.fields.map((field) => <PropertyItem key={field.key} label={field.label}>{recordLabel(record.kind, field.key, field.value) ?? "—"}</PropertyItem>)}
      </PropertyList></div>}
      {details?.structured_record_restricted && <p className="text-sm text-muted-foreground">Nemate ovlast za podatke povezanog zapisa.</p>}
    </section>
    {showPreview && document && <DocumentBlobPreview key={document.id} radnikId={radnikId} document={document} />}
  </>;
}

export function DocumentBlobPreview({ radnikId, document }: { radnikId: string; document: DokumentDjelatnika }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!document.has_content) return;
    const controller = new AbortController();
    let local: string | null = null;
    dohvatiSadrzajDokumenta(radnikId, document.id, controller.signal).then((blob) => {
      if (!controller.signal.aborted) { local = URL.createObjectURL(blob); setUrl(local); }
    }).catch((failure: unknown) => {
      if (!controller.signal.aborted) setError(errorMessage(failure, "Sadržaj nije dostupan."));
    });
    return () => { controller.abort(); if (local) URL.revokeObjectURL(local); };
  }, [radnikId, document.id, document.has_content]);
  if (!document.has_content) return <p className="p-3 text-sm">Nema povezane datoteke.</p>;
  if (error) return <p role="alert">{error}</p>;
  if (!url) return <p role="status">Učitavanje dokumenta…</p>;
  const name = archiveTitle(document);
  return isPdf(document.mime_type || "") ? <>
    <a className="p-3 underline lg:hidden" href={url} target="_blank" rel="noreferrer">Otvori PDF</a>
    <a className={buttonVariants({ variant: "secondary", size: "sm" })} href={url} download={document.original_filename || name}>Preuzmi</a>
    <ResponsivePdfViewer title={`Pregled dokumenta ${name}`} url={url} filename={document.original_filename || name} iframeClassName="min-h-80 w-full flex-1 rounded border" mobileClassName="h-[70dvh] rounded border" />
  </> : <img src={url} alt={`Sadržaj dokumenta ${name}`} className="max-h-[70vh] w-full rounded object-contain" />;
}
